import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { formatDisplayDate } from '../../utils/formatDate';

const PlatformFavoritesTable = () => {
    const { auth } = useAuth();
    const [items, setItems] = useState([]);
    const [type, setType] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const params = new URLSearchParams({ type, limit: '50' });
            if (search.trim()) params.set('search', search.trim());
            const response = await auth.fetch(`${config.API_BASE_URL}/api/actions/admin/favorites/summary?${params.toString()}`);
            if (!response.ok) throw new Error('Failed to load favorites');
            const data = await response.json();
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch (err) {
            console.error('Error fetching platform favorites:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [auth, type, search]);

    useEffect(() => {
        const timer = setTimeout(fetchItems, 300);
        return () => clearTimeout(timer);
    }, [fetchItems]);

    return (
        <div className="card h-100 radius-8 border-0">
            <div className="card-body p-24">
                <div className="d-flex align-items-center flex-wrap gap-3 justify-content-between mb-16">
                    <div>
                        <h6 className="mb-1 fw-bold text-lg">Platform Customer Favorites</h6>
                        <span className="text-sm text-secondary-light">Most saved venues and content across the platform</span>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            style={{ width: '220px' }}
                            placeholder="Search venues or content"
                            aria-label="Search favorites"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <select
                            className="form-select form-select-sm w-auto"
                            aria-label="Favorite type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        >
                            <option value="all">All</option>
                            <option value="venue">Venues</option>
                            <option value="blog">Content</option>
                        </select>
                    </div>
                </div>

                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">Saved Item</th>
                                <th scope="col">Type</th>
                                <th scope="col">Venue</th>
                                <th scope="col" className="text-center">Total Saves</th>
                                <th scope="col" className="text-center">Unique Savers</th>
                                <th scope="col">Last Saved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-4 text-secondary-light">Loading...</td></tr>
                            ) : error ? (
                                <tr><td colSpan="6" className="text-center py-4 text-danger">Could not load favorites.</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-4 text-secondary-light">No saves found.</td></tr>
                            ) : items.map((item) => (
                                <tr key={`${item.type}-${item.item_id}`}>
                                    <td className="fw-medium">{item.item_title || '—'}</td>
                                    <td>{item.type === 'venue' ? 'Venue' : 'Content'}</td>
                                    <td>{item.venue_name || '—'}</td>
                                    <td className="text-center">{item.saves}</td>
                                    <td className="text-center">{item.unique_savers}</td>
                                    <td>{item.last_saved ? formatDisplayDate(item.last_saved) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PlatformFavoritesTable;
